import React, { useState, useCallback, useMemo, useRef } from 'react';
import { getApiUrl } from '@/config/api';

interface MatchEntry {
  external_id: string;
  external_name: string;
  first_name: string;
  last_name: string;
  provider: string;
  jersey: string;
  position: string;
  suggested_action: 'link' | 'create';
  suggested_user_id: number | null;
  suggested_user_name: string | null;
  suggested_user_email: string | null;
  confidence: number;
  match_type: 'exact_name' | 'fuzzy_name' | 'none';
  hawkin_teams?: string[];
  hawkin_groups?: string[];
  already_linked?: boolean;
  coach_selected?: boolean;
  invite_status?: 'linked' | 'pending' | 'unlinked';
  invite_email?: string | null;
  linked_user_id?: number | null;
}

interface LinkedEntry {
  external_id: string;
  external_name: string;
  linked_user_id: number | null;
  linked_user_name: string | null;
  linked_user_email: string | null;
  coach_selected?: boolean;
  invite_status?: 'linked' | 'pending' | 'unlinked';
  invite_email?: string | null;
}

interface TeamMember {
  id: number;
  name: string;
  email: string;
}

interface CoachTeam {
  id: number;
  name: string;
}

interface MatchPreviewResponse {
  provider: string;
  preview: MatchEntry[];
  already_linked: LinkedEntry[];
  total_athletes: number;
  team_id: number | null;
  team_name: string | null;
  team_members: TeamMember[];
  coach_teams?: CoachTeam[];
}

interface PlayerMatchingModalProps {
  provider: 'hawkin' | 'catapult';
  onClose: () => void;
  onComplete: (results: any) => void;
  onNothingToReview?: () => void;
  manageMode?: boolean;
}

type InviteStatus = 'linked' | 'pending' | 'unlinked';
type RowMode = 'existing' | 'invite';

interface RowState {
  mode: RowMode;
  email: string;
  linkedUserId: number | null;
  editing: boolean; // true when user clicked X on a pending row
  submitted: boolean; // true after user presses Enter/submit on email
}

const STATUS_DOT: Record<InviteStatus, { color: string; label: string }> = {
  linked: { color: 'bg-emerald-500', label: 'Linked' },
  pending: { color: 'bg-amber-500', label: 'Invited' },
  unlinked: { color: 'bg-red-500', label: 'Unlinked' },
};

const PlayerMatchingModal: React.FC<PlayerMatchingModalProps> = ({
  provider,
  onClose,
  onComplete,
  onNothingToReview,
  manageMode = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [previewData, setPreviewData] = useState<MatchPreviewResponse | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);

  // Per-row state for mode/email/link changes
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});

  const fetchPreview = useCallback(async (teamId?: number | null) => {
    const access = localStorage.getItem('access');
    if (!access) return;

    setLoading(true);
    setError('');
    try {
      const body: Record<string, any> = { provider, force_full_preview: true };
      if (teamId) body.team_id = teamId;

      const res = await fetch(getApiUrl('/api/wearables/match-preview/'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${access}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const data: MatchPreviewResponse = await res.json();
      if (!res.ok) {
        setError((data as any).error || 'Failed to load preview');
        return;
      }
      setPreviewData(data);

      if (data.team_id && !selectedTeamId) {
        setSelectedTeamId(data.team_id);
      }

      const allIds = new Set<string>();
      for (const entry of data.preview) allIds.add(entry.external_id);
      for (const entry of data.already_linked) allIds.add(entry.external_id);

      if (allIds.size === 0) {
        if (onNothingToReview) onNothingToReview();
        return;
      }

      const coachSelected = new Set<string>();
      for (const entry of data.preview) {
        if (entry.coach_selected) coachSelected.add(entry.external_id);
      }
      for (const entry of data.already_linked) {
        if (entry.coach_selected) coachSelected.add(entry.external_id);
      }
      setSelectedIds(coachSelected.size > 0 ? coachSelected : allIds);
    } catch (err: any) {
      setError(err.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [provider, onNothingToReview, selectedTeamId]);

  React.useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  const handleTeamChange = (newTeamId: number) => {
    setSelectedTeamId(newTeamId);
    setSelectedIds(new Set());
    setRowStates({});
    fetchPreview(newTeamId);
  };

  // Unified athlete list
  const allAthletes = useMemo(() => {
    if (!previewData) return [];
    const seen = new Set<string>();
    const result: Array<{
      external_id: string;
      name: string;
      hawkin_teams?: string[];
      inviteStatus: InviteStatus;
      inviteEmail: string | null;
      linkedUserId: number | null;
      suggestedUserId: number | null;
      linkedEmail: string | null;
      linkedName: string | null;
      firstName: string;
      lastName: string;
    }> = [];

    for (const entry of previewData.preview) {
      if (seen.has(entry.external_id)) continue;
      seen.add(entry.external_id);
      const status = (entry.invite_status || 'unlinked') as InviteStatus;
      result.push({
        external_id: entry.external_id,
        name: entry.external_name || `${entry.first_name} ${entry.last_name}`.trim(),
        hawkin_teams: entry.hawkin_teams,
        inviteStatus: status,
        inviteEmail: entry.invite_email || null,
        linkedUserId: entry.linked_user_id ?? null,
        suggestedUserId: entry.suggested_user_id,
        linkedEmail: status === 'linked' ? entry.suggested_user_email : null,
        linkedName: status === 'linked' ? entry.suggested_user_name : null,
        firstName: entry.first_name,
        lastName: entry.last_name,
      });
    }

    for (const entry of previewData.already_linked) {
      if (seen.has(entry.external_id)) continue;
      seen.add(entry.external_id);
      const status = (entry.invite_status || 'unlinked') as InviteStatus;
      result.push({
        external_id: entry.external_id,
        name: entry.external_name,
        inviteStatus: status,
        inviteEmail: entry.invite_email || null,
        linkedUserId: entry.linked_user_id,
        suggestedUserId: null,
        linkedEmail: status === 'linked' ? entry.linked_user_email : null,
        linkedName: status === 'linked' ? entry.linked_user_name : null,
        firstName: entry.external_name.split(' ')[0] || '',
        lastName: entry.external_name.split(' ').slice(1).join(' ') || '',
      });
    }

    return result;
  }, [previewData]);

  const hawkinTeamLabels = useMemo(() => {
    if (provider !== 'hawkin') return [];
    const labels = new Set<string>();
    for (const a of allAthletes) {
      for (const t of a.hawkin_teams || []) {
        if (typeof t === 'string' && t) labels.add(t);
      }
    }
    return Array.from(labels).sort();
  }, [allAthletes, provider]);

  const filteredAthletes = useMemo(() => {
    if (!searchQuery.trim()) return allAthletes;
    const q = searchQuery.toLowerCase();
    return allAthletes.filter((a) => a.name.toLowerCase().includes(q));
  }, [allAthletes, searchQuery]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(filteredAthletes.map((a) => a.external_id)));
  const deselectAll = () => setSelectedIds(new Set());

  const selectByHawkinTeam = (teamLabel: string) => {
    const ids = allAthletes
      .filter((a) => (a.hawkin_teams || []).includes(teamLabel))
      .map((a) => a.external_id);
    setSelectedIds(new Set(ids));
  };

  const teamMembers = previewData?.team_members || [];
  const coachTeams = previewData?.coach_teams || [];
  const providerLabel = provider === 'hawkin' ? 'Hawkin Dynamics' : 'Catapult';

  const getRowState = (athlete: typeof allAthletes[0]): RowState => {
    if (rowStates[athlete.external_id]) return rowStates[athlete.external_id];
    if (athlete.inviteStatus === 'linked') {
      return { mode: 'existing', email: '', linkedUserId: athlete.linkedUserId, editing: false, submitted: false };
    }
    if (athlete.inviteStatus === 'pending') {
      return { mode: 'invite', email: athlete.inviteEmail || '', linkedUserId: athlete.linkedUserId, editing: false, submitted: false };
    }
    // For unlinked athletes with a fuzzy match suggestion, pre-select "Choose existing"
    if (athlete.suggestedUserId) {
      return { mode: 'existing', email: '', linkedUserId: athlete.suggestedUserId, editing: false, submitted: false };
    }
    return { mode: 'invite', email: '', linkedUserId: null, editing: false, submitted: false };
  };

  const updateRowState = (extId: string, patch: Partial<RowState>) => {
    setRowStates((prev) => ({
      ...prev,
      [extId]: { ...getRowStateById(extId), ...patch },
    }));
  };

  const getRowStateById = (extId: string): RowState => {
    const athlete = allAthletes.find((a) => a.external_id === extId);
    if (!athlete) return { mode: 'invite', email: '', linkedUserId: null, editing: false, submitted: false };
    return getRowState(athlete);
  };

  const getEffectiveStatus = (athlete: typeof allAthletes[0]): InviteStatus => {
    const rs = getRowState(athlete);
    const modified = !!rowStates[athlete.external_id];
    // Only show "linked" for existing-mode if the user explicitly changed it,
    // not just because of a pre-filled fuzzy suggestion.
    if (modified && rs.mode === 'existing' && rs.linkedUserId) return 'linked';
    if (rs.submitted && rs.email.trim()) return 'pending';
    if (rs.editing) return 'unlinked';
    if (athlete.inviteStatus === 'linked') return 'linked';
    if (athlete.inviteStatus === 'pending') return 'pending';
    return 'unlinked';
  };

  const selectedAthletes = useMemo(
    () => allAthletes.filter((a) => selectedIds.has(a.external_id)),
    [allAthletes, selectedIds],
  );

  const unlinkedCount = selectedAthletes.filter((a) => getEffectiveStatus(a) === 'unlinked').length;
  const pendingCount = selectedAthletes.filter((a) => getEffectiveStatus(a) === 'pending').length;

  const handleSubmit = async () => {
    const access = localStorage.getItem('access');
    if (!access) return;

    setSubmitting(true);
    setError('');

    const confirmations: Array<Record<string, any>> = [];
    for (const athlete of selectedAthletes) {
      const rs = getRowState(athlete);
      const modified = !!rowStates[athlete.external_id];

      // Already linked/pending with no local changes — always skip
      if (athlete.inviteStatus === 'linked' && !modified) continue;
      if (athlete.inviteStatus === 'pending' && !modified) continue;

      // Coach clicked unlink on a previously linked/pending athlete
      if (modified && (athlete.inviteStatus === 'linked' || athlete.inviteStatus === 'pending')
          && !rs.linkedUserId && !rs.submitted) {
        confirmations.push({
          external_id: athlete.external_id,
          action: 'unlink',
          user_id: athlete.linkedUserId,
        });
        continue;
      }

      // In manage mode, skip unlinked rows the coach didn't touch —
      // don't auto-accept fuzzy suggestions or create unwanted placeholders.
      // On initial connect, unmodified rows should proceed (accept suggestions / create placeholders).
      if (manageMode && athlete.inviteStatus === 'unlinked' && !modified) continue;

      const stagedEmail = rs.submitted ? rs.email.trim() : '';

      if (rs.mode === 'existing' && rs.linkedUserId && (modified || !manageMode)) {
        confirmations.push({
          external_id: athlete.external_id,
          action: 'link',
          user_id: rs.linkedUserId,
          first_name: athlete.firstName,
          last_name: athlete.lastName,
        });
      } else if (athlete.linkedUserId && stagedEmail) {
        const emailChanged = stagedEmail !== (athlete.inviteEmail || '').trim();
        // If email unchanged, intentionally push nothing — row is a no-op.
        // The else-if chain prevents fallthrough to 'create'.
        if (emailChanged) {
          confirmations.push({
            external_id: athlete.external_id,
            action: 'reinvite',
            user_id: athlete.linkedUserId,
            email: stagedEmail,
            first_name: athlete.firstName,
            last_name: athlete.lastName,
          });
        }
      } else if (athlete.linkedUserId && !stagedEmail) {
        continue;
      } else if (stagedEmail) {
        confirmations.push({
          external_id: athlete.external_id,
          action: 'create',
          email: stagedEmail,
          first_name: athlete.firstName,
          last_name: athlete.lastName,
        });
      } else if (!athlete.linkedUserId) {
        confirmations.push({
          external_id: athlete.external_id,
          action: 'create',
          first_name: athlete.firstName,
          last_name: athlete.lastName,
        });
      }
    }

    try {
      const res = await fetch(getApiUrl('/api/wearables/confirm-matches/'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${access}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider,
          team_id: selectedTeamId || previewData?.team_id,
          selected_external_ids: Array.from(selectedIds),
          confirmations,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError((data as any).error || 'Failed to confirm matches');
        return;
      }
      onComplete(data.results);
    } catch (err: any) {
      setError(err.message || 'Failed to confirm');
    } finally {
      setSubmitting(false);
    }
  };

  const renderTeamPicker = () => {
    if (coachTeams.length <= 1) return null;
    return (
      <div className="px-6 pb-2">
        <label className="block text-xs font-medium cgm-text-secondary mb-1">
          Configuring for team
        </label>
        <select
          value={selectedTeamId || ''}
          onChange={(e) => handleTeamChange(Number(e.target.value))}
          className="cgm-input w-full text-sm px-3 py-1.5"
        >
          {coachTeams.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>
    );
  };

  const renderRow = (athlete: typeof allAthletes[0]) => {
    const checked = selectedIds.has(athlete.external_id);
    const rs = getRowState(athlete);
    const effectiveStatus = getEffectiveStatus(athlete);
    const dot = STATUS_DOT[effectiveStatus];
    const isPending = (athlete.inviteStatus === 'pending' && !rs.editing) || (rs.submitted && rs.email.trim());
    const isLinked = athlete.inviteStatus === 'linked' && !rowStates[athlete.external_id];

    return (
      <li
        key={athlete.external_id}
        className={`flex items-center gap-2 py-2.5 ${checked ? '' : 'opacity-40'}`}
      >
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={checked}
          onChange={() => toggleSelect(athlete.external_id)}
          className="h-4 w-4 rounded shrink-0 accent-blue-600"
        />

        {/* Status dot */}
        <span
          className={`shrink-0 w-2.5 h-2.5 rounded-full ${dot.color}`}
          title={dot.label}
        />

        {/* Player name */}
        <span className={`text-sm font-medium truncate w-32 shrink-0 ${checked ? 'cgm-text-primary' : 'cgm-text-secondary'}`}>
          {athlete.name || 'Unknown'}
        </span>

        {!checked ? (
          <div className="flex-1" />
        ) : isLinked ? (
          /* Linked: read-only email display with unlink button */
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="text-sm cgm-text-secondary truncate">
              {athlete.linkedEmail}
            </span>
            <button
              onClick={() => updateRowState(athlete.external_id, {
                mode: 'invite',
                email: '',
                linkedUserId: null,
                editing: true,
                submitted: false,
              })}
              className="shrink-0 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
              title="Unlink this player"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : isPending ? (
          /* Pending: hardened "Invite sent" display with red X */
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="text-sm text-amber-600 dark:text-amber-400 truncate">
              Invite sent to {rs.submitted ? rs.email : athlete.inviteEmail}
            </span>
            <button
              onClick={() => updateRowState(athlete.external_id, {
                editing: true,
                email: rs.submitted ? rs.email : (athlete.inviteEmail || ''),
                mode: 'invite',
                linkedUserId: athlete.linkedUserId,
                submitted: false,
              })}
              className="shrink-0 text-red-500 hover:text-red-700 transition-colors"
              title="Change invite email"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          /* Unlinked / editing: mode selector + action */
          <div className="flex-1 min-w-0 flex items-center gap-2">
            {/* Mode selector */}
            {teamMembers.length > 0 && (
              <select
                value={rs.mode}
                onChange={(e) => updateRowState(athlete.external_id, {
                  mode: e.target.value as RowMode,
                  email: '',
                  linkedUserId: null,
                  editing: rs.editing,
                  submitted: false,
                })}
                className="cgm-input text-xs px-2 py-1 shrink-0 w-36"
              >
                <option value="invite">Invite new user</option>
                <option value="existing">Choose existing</option>
              </select>
            )}

            {/* Action column */}
            {rs.mode === 'existing' ? (
              <select
                className="cgm-input text-sm px-2 py-1 flex-1 min-w-0"
                value={rs.linkedUserId || ''}
                onChange={(e) => {
                  const uid = Number(e.target.value);
                  updateRowState(athlete.external_id, {
                    linkedUserId: uid || null,
                    mode: 'existing',
                    editing: rs.editing,
                    email: '',
                    submitted: false,
                  });
                }}
              >
                <option value="">Select a player…</option>
                {teamMembers.map((m) => (
                  <option key={m.id} value={m.id}>{m.name} ({m.email})</option>
                ))}
              </select>
            ) : (
              <EmailInput
                value={rs.email}
                onChange={(val) => updateRowState(athlete.external_id, { ...rs, email: val, submitted: false })}
                onSubmit={(val) => updateRowState(athlete.external_id, { ...rs, email: val, submitted: true, editing: false })}
                onCancel={rs.editing ? () => {
                  setRowStates((prev) => {
                    const next = { ...prev };
                    delete next[athlete.external_id];
                    return next;
                  });
                } : undefined}
                placeholder="Enter email to invite…"
              />
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="cgm-modal-backdrop">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="cgm-card cgm-modal-panel p-0 max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">

          {/* Header */}
          <div className="text-center px-6 pt-6 pb-2">
            <h3 className="text-xl cgm-text-primary mb-1.5">
              {loading
                ? `Connecting to ${providerLabel}…`
                : `Manage ${providerLabel} Players`}
            </h3>
            {!loading && allAthletes.length > 0 && (
              <p className="cgm-text-secondary text-sm">
                {allAthletes.length} player{allAthletes.length !== 1 ? 's' : ''} on this account.
                {' '}Check the ones on your team and connect their accounts.
              </p>
            )}
          </div>

          {renderTeamPicker()}

          {error && (
            <div className="mx-6 mb-3">
              <div className="cgm-alert-error px-3 py-2 rounded-md text-sm">{error}</div>
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6">
            {loading && (
              <div className="flex flex-col items-center justify-center py-16 cgm-text-secondary">
                <div className="animate-spin rounded-full h-6 w-6 mb-3 cgm-spinner border-2" />
                <span className="text-sm">Loading players…</span>
              </div>
            )}

            {!loading && allAthletes.length === 0 && !error && (
              <div className="text-center py-8 cgm-text-muted text-sm">
                No {providerLabel} account connected, or no players found.
                <br />
                Connect via the Connect to Wearable page first.
              </div>
            )}

            {!loading && allAthletes.length > 0 && (
              <>
                {/* Search + bulk actions */}
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="text"
                    placeholder="Search players…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="cgm-input flex-1 text-sm px-3 py-1.5"
                  />
                  <button onClick={selectAll} className="text-xs font-medium cgm-text-muted hover:cgm-text-primary transition-colors px-2 py-1 underline decoration-dotted underline-offset-2 whitespace-nowrap">All</button>
                  <button onClick={deselectAll} className="text-xs font-medium cgm-text-muted hover:cgm-text-primary transition-colors px-2 py-1 underline decoration-dotted underline-offset-2 whitespace-nowrap">None</button>
                </div>

                {/* Hawkin team group buttons */}
                {hawkinTeamLabels.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <span className="text-xs cgm-text-muted py-1">Groups:</span>
                    {hawkinTeamLabels.map((label) => (
                      <button
                        key={label}
                        onClick={() => selectByHawkinTeam(label)}
                        className="text-xs px-2 py-1 rounded-full border cgm-text-secondary hover:cgm-text-primary transition-colors"
                        style={{ borderColor: 'var(--card-border)' }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Selection + status counts */}
                <div className="flex items-center gap-3 text-xs cgm-text-muted mb-2">
                  <span>{selectedIds.size} of {allAthletes.length} selected</span>
                  {unlinkedCount > 0 && (
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
                      {unlinkedCount} unlinked
                    </span>
                  )}
                  {pendingCount > 0 && (
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                      {pendingCount} pending
                    </span>
                  )}
                </div>

                {/* Player list */}
                <ul className="divide-y" style={{ borderColor: 'var(--card-border)' }}>
                  {filteredAthletes.map(renderRow)}
                </ul>
              </>
            )}
          </div>

          {/* Footer */}
          {!loading && allAthletes.length > 0 && (
            <div className="px-6 pt-3 pb-6 space-y-2">
              <button
                onClick={handleSubmit}
                disabled={submitting || selectedIds.size === 0}
                className={`cgm-button w-full py-3 px-4 flex items-center justify-center ${selectedIds.size === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2" />
                    Saving…
                  </>
                ) : (
                  `Confirm ${selectedIds.size} Player${selectedIds.size !== 1 ? 's' : ''}`
                )}
              </button>
              <button
                onClick={onClose}
                className="cgm-cancel-button w-full py-3 px-4 flex items-center justify-center rounded-lg font-medium"
              >
                Cancel
              </button>
            </div>
          )}

          {!loading && allAthletes.length === 0 && !error && (
            <div className="px-6 pt-3 pb-6">
              <button
                onClick={onClose}
                className="cgm-button w-full py-3 px-4 flex items-center justify-center"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/** Inline email input with submit button. Submits on Enter or button click, cancels on Escape. */
const EmailInput: React.FC<{
  value: string;
  onChange: (val: string) => void;
  onSubmit: (val: string) => void;
  onCancel?: () => void;
  placeholder?: string;
}> = ({ value, onChange, onSubmit, onCancel, placeholder }) => {
  const ref = useRef<HTMLInputElement>(null);
  const canSubmit = value.trim().length > 0 && value.includes('@');

  const doSubmit = () => {
    if (!canSubmit) return;
    onSubmit(value.trim());
    ref.current?.blur();
  };

  return (
    <div className="flex-1 min-w-0 flex items-center gap-0 relative">
      <input
        ref={ref}
        type="email"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            doSubmit();
          } else if (e.key === 'Escape' && onCancel) {
            e.preventDefault();
            onCancel();
            ref.current?.blur();
          }
        }}
        onBlur={() => {
          if (onCancel && !value.trim()) onCancel();
        }}
        className="cgm-input text-sm px-2 py-1 flex-1 min-w-0 rounded-r-none border-r-0"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); doSubmit(); }}
        disabled={!canSubmit}
        className={`shrink-0 px-2.5 py-1 rounded-r-md border text-sm font-medium transition-colors ${
          canSubmit
            ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-300 dark:border-gray-600 cursor-not-allowed'
        }`}
        title="Send invite"
        style={{ height: '100%' }}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
      </button>
    </div>
  );
};

export default PlayerMatchingModal;
